import React, { useState, useEffect } from 'react';
import { EventDropLogo } from '../components/EventDropLogo';
import { GoogleLogo, ArrowLeft, EnvelopeSimple, Lock, User, Eye, EyeSlash } from '@phosphor-icons/react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { resolvePostAuthPath } from '../utils/postAuthRoute';

const AUTH_URL = 'https://auth.emergentagent.com';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Mode 1 — Atmospheric Editorial (walnut / parchment / gold) ───
const PAPER_BG  = '#1c1108';
const PAPER_HI  = '#2a190a';
const INK       = '#FFE7A8';
const INK_MUTED = 'rgba(255,235,210,0.62)';
const INK_FAINT = 'rgba(255,235,210,0.38)';
const GOLD      = '#D4AF5E';
const HAIR      = 'rgba(212,170,82,0.32)';
const SERIF     = 'Georgia, "Times New Roman", serif';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' or 'register'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  // Supplier-aware context: either explicit ?role=supplier OR the `from`
  // points at a supplier page (e.g. /supplier/onboard).
  const roleParam = searchParams.get('role');
  const intendedFrom = location.state?.from || searchParams.get('from') || searchParams.get('next') || null;
  const isSupplierFlow = roleParam === 'supplier' || (intendedFrom || '').startsWith('/supplier');

  // Already-signed-in users hitting /login (e.g. via browser back) get
  // routed through to wherever the post-auth resolver decides — fixes
  // the "back button looks like it logged me out" UX bug. Auth state
  // was never lost; LoginPage just used to render the form regardless.
  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    let cancelled = false;
    (async () => {
      const target = await resolvePostAuthPath({
        intendedFrom,
        viewOverride: searchParams.get('view'),
      });
      if (!cancelled) navigate(target, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  // Optional "you've been signed out" toast — feels intentional, not broken.
  useEffect(() => {
    if (searchParams.get('logged_out') === 'true') {
      toast.success("You've been signed out");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const afterLogin = async () => {
    // If the user started from somewhere specific (e.g. /supplier/onboard),
    // respect that. Otherwise, let the resolver pick host vs supplier.
    const target = await resolvePostAuthPath({
      intendedFrom,
      viewOverride: searchParams.get('view'),
    });
    navigate(target, { replace: true });
  };

  const handleGoogleLogin = () => {
    setLoading(true);
    setError('');
    const popupRedirect = `${window.location.origin}/auth-callback`;
    const authUrl = `${AUTH_URL}?redirect=${encodeURIComponent(popupRedirect)}`;

    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const popup = window.open(authUrl, 'eventdrop_auth', `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`);

    if (!popup || popup.closed) {
      window.location.href = authUrl;
      return;
    }

    const handleMessage = async (event) => {
      if (event.data?.type === 'eventdrop_auth' && event.data?.session_id) {
        window.removeEventListener('message', handleMessage);
        clearInterval(pollTimer);
        try {
          const response = await axios.post(`${API}/auth/session`, { session_id: event.data.session_id });
          login(response.data.user, response.data.session_token);
          await afterLogin();
        } catch (err) {
          setError('Sign in failed. Please try again.');
          setLoading(false);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    const pollTimer = setInterval(() => {
      if (!popup || popup.closed) {
        clearInterval(pollTimer);
        window.removeEventListener('message', handleMessage);
        setLoading(false);
      }
    }, 500);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'register' ? `${API}/auth/register` : `${API}/auth/login-email`;
      const payload = mode === 'register' ? { email, password, name } : { email, password };
      const res = await axios.post(endpoint, payload);
      login(res.data.user, res.data.session_token);
      toast.success(mode === 'register' ? 'Account created!' : 'Signed in!');
      await afterLogin();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `radial-gradient(900px 600px at 50% -100px, ${PAPER_HI} 0%, ${PAPER_BG} 60%, #140a04 100%)`,
        color: INK,
        fontFamily: SERIF,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 22px',
      }}
    >
      <div style={{ width: '100%', maxWidth: 400 }} data-testid="login-page">
        <button
          onClick={() => navigate('/')}
          data-testid="back-to-home"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: INK_MUTED, fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 13, letterSpacing: '0.04em', padding: 0, marginBottom: 28,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = INK; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = INK_MUTED; }}
        >
          <ArrowLeft size={14} weight="bold" /> Back
        </button>

        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            <EventDropLogo size={44} />
          </div>
          <div style={{
            fontSize: 11, fontWeight: 800, letterSpacing: '0.32em',
            textTransform: 'uppercase', color: GOLD, marginBottom: 12,
          }}>
            ✦ {mode === 'register' ? 'BEGIN' : 'RETURN'}
          </div>
          <h1
            data-testid="login-heading"
            style={{
              fontFamily: SERIF, fontWeight: 500,
              letterSpacing: '-0.01em',
              fontSize: 'clamp(26px, 5vw, 34px)', lineHeight: 1.1,
              margin: 0, color: INK,
            }}
          >
            {mode === 'register'
              ? (isSupplierFlow ? 'Create your supplier account' : 'Create your account')
              : 'Welcome back'}
          </h1>
          <p
            data-testid="login-subheading"
            style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 14, color: INK_MUTED, lineHeight: 1.55,
              margin: '10px 0 0',
            }}
          >
            {mode === 'register'
              ? (isSupplierFlow ? 'Start receiving leads in minutes' : 'Get started with EventDrop')
              : (isSupplierFlow
                  ? 'Sign in to manage your bookings and availability'
                  : 'Sign in to manage your events')}
          </p>
        </div>

        {/* Google button — ghost-pill, no hover lift */}
        <button
          onClick={handleGoogleLogin}
          disabled={loading}
          data-testid="google-login-button"
          style={{
            width: '100%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 12,
            padding: '13px 18px',
            borderRadius: 12,
            background: 'rgba(20,12,6,0.72)',
            border: `1px solid ${HAIR}`,
            color: INK,
            fontFamily: SERIF, fontWeight: 600, fontSize: 14,
            letterSpacing: '0.02em',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.55 : 1,
            marginBottom: 20,
            transition: 'background 220ms ease, border-color 220ms ease',
          }}
          onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.background = 'rgba(212,170,82,0.08)'; e.currentTarget.style.borderColor = GOLD; } }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(20,12,6,0.72)'; e.currentTarget.style.borderColor = HAIR; }}
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: HAIR }} />
          <span style={{
            fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
            color: INK_FAINT, letterSpacing: '0.18em', textTransform: 'uppercase',
          }}>or</span>
          <div style={{ flex: 1, height: 1, background: HAIR }} />
        </div>

        {/* Email/password form */}
        <form
          onSubmit={handleEmailSubmit}
          data-testid="email-auth-form"
          style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        >
          {mode === 'register' && (
            <FieldShell icon={<User size={15} weight="bold" />}>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Full name"
                required
                data-testid="register-name-input"
                style={inputBase}
              />
            </FieldShell>
          )}
          <FieldShell icon={<EnvelopeSimple size={15} weight="bold" />}>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email address"
              required
              data-testid="email-input"
              style={inputBase}
            />
          </FieldShell>
          <FieldShell icon={<Lock size={15} weight="bold" />} trailing={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: INK_FAINT, padding: 0, display: 'inline-flex',
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeSlash size={15} /> : <Eye size={15} />}
            </button>
          }>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={mode === 'register' ? 'Create password (6+ chars)' : 'Password'}
              required
              minLength={mode === 'register' ? 6 : undefined}
              data-testid="password-input"
              style={inputBase}
            />
          </FieldShell>

          {error && (
            <p
              data-testid="auth-error"
              style={{
                fontFamily: SERIF, fontStyle: 'italic',
                fontSize: 12.5, color: '#FFAA90',
                margin: '4px 4px 0',
              }}
            >{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="email-submit-button"
            style={{
              width: '100%', padding: '14px 22px',
              borderRadius: 12,
              background: loading
                ? 'rgba(212,170,82,0.18)'
                : 'linear-gradient(135deg, #D4AF5E 0%, #B8923F 100%)',
              color: loading ? 'rgba(255,235,210,0.45)' : '#0F0B1A',
              border: 'none',
              fontFamily: SERIF, fontWeight: 700, fontSize: 14.5,
              letterSpacing: '0.02em',
              cursor: loading ? 'not-allowed' : 'pointer',
              boxShadow: loading ? 'none' : '0 10px 30px rgba(0,0,0,0.35)',
              marginTop: 8,
              transition: 'transform 240ms ease',
            }}
            onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            {loading ? 'Please wait…' : (mode === 'register' ? 'Create account' : 'Sign in')}
          </button>
        </form>

        {/* Toggle mode */}
        <p style={{
          textAlign: 'center', fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 13, color: INK_MUTED, margin: '20px 0 0',
        }}>
          {mode === 'login' ? (
            <>Don't have an account?{' '}
              <button
                onClick={() => { setMode('register'); setError(''); }}
                data-testid="switch-to-register"
                style={toggleLink}
              >Sign up</button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); }}
                data-testid="switch-to-login"
                style={toggleLink}
              >Sign in</button>
            </>
          )}
        </p>

        {!isSupplierFlow && (
          <div style={{
            marginTop: 26, paddingTop: 20,
            borderTop: `1px solid ${HAIR}`, textAlign: 'center',
          }}>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, color: INK_FAINT, margin: '0 0 6px',
            }}>Are you a wedding / event supplier?</p>
            <button
              onClick={() => navigate('/supplier/onboard')}
              data-testid="login-supplier-cta"
              style={toggleLink}
            >Join as a supplier →</button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── editorial form primitives ─── */

const inputBase = {
  flex: 1, background: 'transparent', border: 'none', outline: 'none',
  color: INK, fontFamily: SERIF, fontStyle: 'italic',
  fontSize: 15, padding: '12px 4px',
  width: '100%',
};

const toggleLink = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  color: GOLD, fontFamily: SERIF, fontWeight: 600, fontSize: 13,
  letterSpacing: '0.02em', padding: 0,
  textDecoration: 'none',
};

const FieldShell = ({ icon, trailing, children }) => (
  <div
    style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '4px 14px',
      borderRadius: 12,
      background: 'rgba(20,12,6,0.78)',
      border: `1px solid ${HAIR}`,
      boxShadow: 'inset 0 1px 0 rgba(212,170,82,0.08)',
      transition: 'border-color 220ms ease',
    }}
    onFocusCapture={(e) => { e.currentTarget.style.borderColor = GOLD; }}
    onBlurCapture={(e) => { e.currentTarget.style.borderColor = HAIR; }}
  >
    <span style={{ color: INK_FAINT, display: 'inline-flex', flexShrink: 0 }}>{icon}</span>
    {children}
    {trailing && <span style={{ flexShrink: 0 }}>{trailing}</span>}
  </div>
);

export default LoginPage;
